import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../api";
import { HR_MODULES } from "../hrConfig";

function emptyForm(fields) {
  const f = {};
  fields.forEach((field) => { f[field.name] = ""; });
  return f;
}

// --- CSV import helpers -----------------------------------------------

// Minimal RFC4180-ish CSV parser: handles quoted fields, escaped quotes,
// commas/newlines inside quotes, and CRLF or LF line endings.
function parseCSV(text) {
  const rows = [];
  let i = 0, field = "", row = [], inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
    } else if (c === "\r") {
      // skip, \n (if present) handles the line break
    } else {
      field += c;
    }
    i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

function normalize(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Match a CSV header to a configured field by exact name or label match
// (case/punctuation-insensitive), so CSVs exported with either the
// internal field name ("txn_date") or the on-screen label ("Date") work.
function matchField(header, fields) {
  const h = normalize(header);
  return fields.find((f) => normalize(f.name) === h || normalize(f.label) === h);
}

// Coerce a raw CSV cell into the shape the field expects (e.g. turn
// "Yes"/"No" into the 1/0 backed by a select field's options).
function coerceCell(field, rawValue) {
  let val = (rawValue ?? "").trim();
  if (field.type === "select" && Array.isArray(field.options)) {
    const hasObjectOptions = field.options.some((o) => typeof o === "object");
    if (hasObjectOptions) {
      const match = field.options.find(
        (o) => String(o.label).toLowerCase() === val.toLowerCase() || String(o.value) === val
      );
      if (match) val = match.value;
    }
  }
  return val;
}

export default function HRModule() {
  const { resource } = useParams();
  const config = HR_MODULES[resource];

  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(config ? emptyForm(config.fields) : {});
  const [search, setSearch] = useState("");

  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => api.hr.list(resource).then(setRows).catch((e) => setError(e.message));
  useEffect(() => { if (config) load(); /* eslint-disable-next-line */ }, [resource]);

  if (!config) return <Navigate to="/admin/hr" replace />;

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm(config.fields));
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row[config.pk]);
    const f = {};
    config.fields.forEach((field) => { f[field.name] = row[field.name] ?? ""; });
    setForm(f);
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.hr.update(resource, editingId, form);
      else await api.hr.create(resource, form);
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this record?")) return;
    await api.hr.remove(resource, id);
    load();
  };

  const openImport = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    setError("");
    setImportSummary(null);

    const text = await file.text();
    const parsed = parseCSV(text);
    if (parsed.length < 2) {
      setError("That CSV doesn't have any data rows.");
      return;
    }

    const headers = parsed[0].map((h) => h.trim());
    const mapped = headers.map((h) => matchField(h, config.fields));
    const unmatchedHeaders = headers.filter((_, idx) => !mapped[idx]);
    const dataRows = parsed.slice(1).filter((r) => r.some((c) => c.trim() !== ""));

    setImporting(true);
    let success = 0;
    const failedRows = [];

    for (const r of dataRows) {
      const obj = {};
      mapped.forEach((field, idx) => {
        if (!field) return;
        obj[field.name] = coerceCell(field, r[idx]);
      });
      const missingRequired = config.fields
        .filter((f) => f.required)
        .find((f) => obj[f.name] === undefined || obj[f.name] === "");
      if (missingRequired) {
        failedRows.push({ row: r, error: `Missing required field: ${missingRequired.label}` });
        continue;
      }
      try {
        await api.hr.create(resource, obj);
        success++;
      } catch (err) {
        failedRows.push({ row: r, error: err.message });
      }
    }

    setImporting(false);
    setImportSummary({
      totalRows: dataRows.length,
      success,
      failed: failedRows.length,
      failedRows,
      unmatchedHeaders,
    });
    load();
  };

  const filtered = search
    ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(search.toLowerCase()))
    : rows;

  const columnLabel = (col) => config.fields.find((f) => f.name === col)?.label || col;

  return (
    <div>
      <Link to="/admin/hr" className="text-sm hover:underline" style={{ color: "#154E93" }}>← Back to HR Management</Link>
      <div className="flex items-center justify-between mt-2 mb-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>{config.label}</h1>
          <p className="text-sm text-gray-500">{rows.length} records</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportFile}
          />
          <button
            onClick={openImport}
            disabled={importing}
            className="px-4 py-2 rounded text-sm font-medium border"
            style={{ color: "#154E93", borderColor: "#154E93" }}
          >
            {importing ? "Importing…" : "Import CSV"}
          </button>
          <button onClick={openNew} className="px-4 py-2 text-white rounded text-sm font-medium" style={{ backgroundColor: "#154E93" }}>
            + Add
          </button>
        </div>
      </div>

      {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      {importSummary && (
        <div className="mb-4 text-sm bg-blue-50 text-blue-900 px-3 py-2 rounded space-y-1">
          <div>
            Imported {importSummary.success} of {importSummary.totalRows} rows.
            {importSummary.failed > 0 && ` ${importSummary.failed} row(s) failed.`}
          </div>
          {importSummary.unmatchedHeaders.length > 0 && (
            <div className="text-xs text-blue-700">
              Unmatched columns (ignored): {importSummary.unmatchedHeaders.join(", ")}
            </div>
          )}
          {importSummary.failedRows.length > 0 && (
            <details className="text-xs text-blue-700">
              <summary className="cursor-pointer">Show failed rows</summary>
              <ul className="mt-1 list-disc list-inside">
                {importSummary.failedRows.slice(0, 20).map((f, idx) => (
                  <li key={idx}>{f.row.join(", ")} — {f.error}</li>
                ))}
              </ul>
              {importSummary.failedRows.length > 20 && (
                <div>…and {importSummary.failedRows.length - 20} more.</div>
              )}
            </details>
          )}
        </div>
      )}

      <input
        placeholder="Search records…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 w-full max-w-md border rounded px-3 py-2 text-sm"
      />

      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead style={{ backgroundColor: "#154E93" }}>
            <tr className="text-white text-left">
              {config.columns.map((col) => (
                <th key={col} className="px-3 py-2">{columnLabel(col)}</th>
              ))}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row[config.pk]} className="border-b last:border-0 hover:bg-gray-50">
                {config.columns.map((col) => (
                  <td key={col} className="px-3 py-2">{String(row[col] ?? "")}</td>
                ))}
                <td className="px-3 py-2 space-x-2">
                  <button onClick={() => openEdit(row)} className="text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => remove(row[config.pk])} className="text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={config.columns.length + 1} className="px-3 py-6 text-center text-gray-400">No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModalOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-lg font-bold mb-4" style={{ color: "#154E93" }}>
              {editingId ? `Edit ${config.label}` : `Add ${config.label}`}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {config.fields.map((field) => (
                <div key={field.name} className={field.type === "textarea" ? "col-span-2" : ""}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                  {field.type === "select" ? (
                    <select
                      required={!!field.required}
                      value={form[field.name]}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">—</option>
                      {field.options.map((opt) =>
                        typeof opt === "object" ? (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ) : (
                          <option key={opt} value={opt}>{opt}</option>
                        )
                      )}
                    </select>
                  ) : field.type === "textarea" ? (
                    <textarea
                      required={!!field.required}
                      value={form[field.name]}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      rows={2}
                    />
                  ) : (
                    <input
                      type={field.type}
                      step={field.type === "number" ? "any" : undefined}
                      required={!!field.required}
                      placeholder={field.placeholder}
                      value={form[field.name]}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded border">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm rounded text-white" style={{ backgroundColor: "#154E93" }}>Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
